import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { FadeAlert } from "../Global/Transitions";
import MemberDto from "../../Models/MemberDto";
import { FadeModal } from "../Global/Transitions";
import { useMsal } from "@azure/msal-react";
import { getprojectforuser } from "../../Services/PtaasApiService";
import { InviteUsers, UpdateUserDetails, RevokeAccess } from "../../Services/AccessControlService";
import { useUser } from "../Global/RoleContext";
import { v4 as uuidv4 } from "uuid";
import UserDetailsForm from "./UserDetailsForm";
import RolesForm, { RoleObj } from "./RolesForm";
import Completion from "./UserCompletion";



type Props = {
  show: boolean;
  onHide: () => void;
  onSave: (user?: Partial<MemberDto>) => Promise<void> | void;
  initialUser?: Partial<MemberDto>;
  existingEmails: string[];
  onAddMember: (member: MemberDto) => void;
  onSync: () => Promise<void>;
};

export default function UsersModal({
  show,
  onHide,
  onSave,
  initialUser,
  existingEmails = [],
  onAddMember,
  onSync,
}: Props) {
  const { instance, accounts } = useMsal();
  const { highestPrivilegeRole } = useUser();
  const editorIsSuperUser = highestPrivilegeRole === "Super User";
  const editorIsProjectManager = highestPrivilegeRole === "Project Manager";

  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState(initialUser?.displayName ?? "");
  const [groupedRoles, setGroupedRoles] = useState<RoleObj[]>([]);
  const [newProject, setNewProject] = useState("");
  const [newRoleChecks, setNewRoleChecks] = useState<Record<string, boolean>>({});
  const [allProjects, setAllProjects] = useState<any[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [creationType, setCreationType] = useState<number>(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [completed, setCompleted] = useState(false);

  const ROLE_OPTIONS_FOR_SELECT = useMemo(() => ["Project Manager", "Project Viewer"], []);

  useEffect(() => {
    setEmail(initialUser?.email || "");
    setError("");
  }, [initialUser, show]);

  const initialIsSuper = useMemo(
    () =>
      Boolean(
        (initialUser?.applicationRole ?? []).some(
          (r: any) => (r.userRole ?? "").trim().toLowerCase() === "super user"
        )
      ),
    [initialUser]
  );
  const [isSuperUser, setIsSuperUser] = useState<boolean>(initialIsSuper);

  const fetchProjects = useCallback(async (): Promise<void> => {
    setLoadingProjects(true);
    try {
      const returned = await getprojectforuser(instance, accounts?.[0]);
      setAllProjects(Array.isArray(returned) ? returned : []);
    } catch (err) {
      console.error("Failed to fetch projects:", err);
      setAllProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  }, [instance, accounts]);

  useEffect(() => {
    if (!show) return;

    setEmail(initialUser?.email ?? "");
    setDisplayName(initialUser?.displayName ?? "");
    setIsSuperUser(initialIsSuper);

    const rolesArr: any[] = initialUser?.applicationRole ?? [];
    const map = new Map<string, { id: string; rolesMap: Map<string, string> }>();

    rolesArr.forEach((r) => {
      const roleNameRaw = (r.userRole ?? "").trim();
      if (!roleNameRaw) return;
      if (roleNameRaw.toLowerCase() === "super user") return;
      const pid = r.projectId ?? "";
      if (!map.has(pid))
        map.set(pid, { id: uuidv4(), rolesMap: new Map<string, string>() });
      const key = roleNameRaw.toLowerCase();
      if (!map.get(pid)!.rolesMap.has(key))
        map.get(pid)!.rolesMap.set(key, roleNameRaw);
    });

    const groups: RoleObj[] = Array.from(map.entries()).map(([projectId, val]) => ({
      id: val.id,
      projectId,
      roles: Array.from(val.rolesMap.values()),
    }));

    setGroupedRoles(groups.length ? groups : []);

    const initChecks: Record<string, boolean> = {};
    ROLE_OPTIONS_FOR_SELECT.forEach((r) => (initChecks[r] = false));
    setNewRoleChecks(initChecks);
    setNewProject("");

    void fetchProjects();
  }, [show, initialUser, initialIsSuper, ROLE_OPTIONS_FOR_SELECT, fetchProjects]);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.trim();
    setEmail(value);
  };

  const isEdit = Boolean(initialUser && initialUser.id);
  const isCreate = !isEdit;

  const handleEmailBlur = () => {
    const exists = existingEmails?.some(
      (e) => e?.toLowerCase() === email?.toLowerCase()
    );
    setError(exists && isCreate ? "User with same email address already present" : "");
  };

  const handleEmailFocus = () => {
    setError("");
  };

  const getProjectName = useCallback(
    (id: string) => {
      const project = allProjects.find((p) => p.ticketno === id);
      const name = project?.projectName ?? "";
      return name || null;
    },
    [allProjects]
  );

  const projectOptions = useMemo(() => {
    const map = new Map<string, { label: string; value: string }>();

    (allProjects || []).forEach((p: any) => {
      const ticket = p?.ticketno ?? p?.ticketNo ?? p?.ticket ?? "";
      const name = p?.projectName ?? p?.projectTitle ?? p?.name ?? "";

      if (ticket) {
        map.set(ticket, { value: ticket, label: name || ticket });
      }
    });

    groupedRoles.forEach((g) => {
      if (g.projectId && !map.has(g.projectId)) {
        map.set(g.projectId, { value: g.projectId, label: g.projectId });
      }
    });

    return Array.from(map.values());
  }, [allProjects, groupedRoles]);

  const checkRoleValidity = (roleList: any[]): boolean => {
    if (!roleList || roleList.length === 0) {
      setError("Role list cannot be empty.");
      return false;
    }
    for (const item of roleList) {
      if (
        (item.userRole ?? "").trim() === "Project Viewer" &&
        !(item.projectId ?? "").trim()
      ) {
        setError("Please select a project for Project Viewer.");
        return false;
      }
    }
    setError(null);
    return true;
  };

  function flattenRolesForPayload(): any[] {
    const createdBy =
      accounts?.[0]?.username ??
      accounts?.[0]?.homeAccountId ??
      "";

    if (isSuperUser) {
      return [
        {
          id: uuidv4(),
          userRole: "Super User",
          projectId: "",
          createdBy,
        },
      ];
    }

    const arr: any[] = [];

    groupedRoles.forEach((g) => {
      g.roles.forEach((roleName) =>
        arr.push({
          id: uuidv4(),
          userRole: roleName,
          projectId: g.projectId ?? "",
          createdBy,
        })
      );
    });

    return arr;
  }


  async function handleSubmit() {
    setError(null);

    const flattened = flattenRolesForPayload();
    if (!checkRoleValidity(flattened)) return;

    if (!displayName.trim()) {
      setError("Display name is required.");
      return;
    }
    if (!email.trim()) {
      setError("Email is required.");
      return;
    }
    if (isCreate && creationType === undefined) {
      setError("Please select creation type: Create user or Invite (SSO).");
      return;
    }

    if (!accounts || accounts.length === 0) {
      setError("You are not authenticated. Please sign in and try again.");
      return;
    }

    setSaving(true);
    try {
      const payload: Partial<MemberDto> = {
        id: initialUser?.id,
        email: email.trim(),
        displayName: displayName.trim(),
        applicationRole: flattened,
        imageData: initialUser?.imageData ?? "",
        imageUrl: undefined,
      };

      if (isCreate) {
        // Backend returns string ID directly
        const newUserId: string = await InviteUsers(
          instance,
          accounts[0],
          displayName.trim(),
          email.trim(),
          flattened,
          creationType
        );

        if (newUserId) {
          const newMember: MemberDto = {
            id: newUserId,
            email: email.trim(),
            displayName: displayName.trim(),
            applicationRole: flattened,
            imageData: "",
            imageUrl: undefined,
          } as MemberDto;
          
          onAddMember(newMember);
          setCompleted(true);
          // No sync needed — we have the real ID
        } else {

          setError("Failed to invite user.");
          return;
        }
      } else {
        await UpdateUserDetails(
          instance,
          accounts[0],
          initialUser!.id!,
          displayName.trim(),
          flattened
        );
        await onSave(payload);
        setCompleted(true);
        setTimeout(() => {
          onSync().catch(console.error);
        }, 2000);
      }
    } catch (err: any) {
      console.error(err);
      setError(err?.message || "An unexpected error occurred while saving the user.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!initialUser?.id) {
      setError("User information missing.");
      return;
    }
    if (!accounts || accounts.length === 0) {
      setError("You are not authenticated. Please sign in and try again.");
      return;
    }
    if (
      !window.confirm(
        "Are you sure you want to delete (revoke access) this user? This action cannot be undone."
      )
    )
      return;
    setSaving(true);
    try {
      await RevokeAccess(instance, accounts[0], initialUser.id);
      setCompleted(true);
    } catch (err: any) {
      console.error(err);
      setError(err?.message || "Failed to delete user.");
    } finally {
      setSaving(false);
    }
  }

  function onContinueAfterComplete() {
    setCompleted(false);
    onHide();
  }

  return (
    <FadeModal
      show={show}
      onHide={onHide}
      title={initialUser ? "Edit User" : "Add User"}
    >
      <Modal.Body>
        {completed ? (
          <Completion
            title={isCreate ? "User Added" : "User Updated / Deleted"}
            text={
              isCreate
                ? "Congratulations! User access has been created successfully."
                : "Operation completed successfully."
            }
            onContinue={onContinueAfterComplete}
          />
        ) : (
          <>
            <FadeAlert show={!!error} dismissFunction={() => setError(null)}>{error}</FadeAlert>

            <UserDetailsForm
              isEdit={isEdit}
              isCreate={isCreate}
              displayName={displayName}
              setDisplayName={setDisplayName}
              email={email}
              handleEmailChange={handleEmailChange}
              handleEmailBlur={handleEmailBlur}
              handleEmailFocus={handleEmailFocus}
              creationType={creationType}
              setCreationType={setCreationType}
              saving={saving}
            />

            <RolesForm
              isSuperUser={isSuperUser}
              setIsSuperUser={setIsSuperUser}
              editorIsSuperUser={editorIsSuperUser}
              groupedRoles={groupedRoles}
              editorIsProjectManager={editorIsProjectManager}
              setGroupedRoles={setGroupedRoles}
              newProject={newProject}
              setNewProject={setNewProject}
              newRoleChecks={newRoleChecks}
              setNewRoleChecks={setNewRoleChecks}
              projectOptions={projectOptions}
              loadingProjects={loadingProjects}
              saving={saving}
              setError={setError}
              getProjectName={getProjectName}
              ROLE_OPTIONS_FOR_SELECT={ROLE_OPTIONS_FOR_SELECT}
            />

            <div className="d-flex pt-2">
              <div style={{ display: "flex", gap: 8 }}>
                {isEdit ? (
                  <Button
                    variant="outline-danger"
                    onClick={handleDelete}
                    disabled={saving}
                  >
                    {saving ? "Deleting…" : "Delete"}
                  </Button>
                ) : (
                  <Button
                    variant="outline-secondary"
                    onClick={onHide}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  variant="secondary"
                  className="ms-3 btn-submit"
                  onClick={handleSubmit}
                  disabled={saving}
                >
                  {saving ? (isCreate ? "Creating…" : "Saving…") : "Submit"}
                </Button>
              </div>
            </div>
          </>
        )}
      </Modal.Body>
    </FadeModal>
  );
}

